// src/app/components/flashOffer/FlashOfferSection.js
import React, { startTransition, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FlashOfferCarousel, SectionHeader } from "@zorni/components-lib";

import useFlashOffer from "./useFlashOffer";
import { getMaxDiscount, toFlashOfferProps } from "./flashOfferService";
import styles from "./FlashOfferSection.module.css";

/**
 * Section « ventes flash » de la page d'accueil
 * (« 01 - home v5 - ventes flash »). Le carrousel vient de la
 * components-lib ; ici on charge les ventes, on les branche sur la
 * navigation et on gère leur absence.
 */
export default function FlashOfferSection() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { offers, expire } = useFlashOffer();

  const cards = useMemo(() => offers.map(toFlashOfferProps), [offers]);

  if (!offers.length) return null;

  const handleBook = (card) => {
    const offer = offers.find((o) => o.id === card.id);
    if (!offer) return;
    startTransition(() => {
      // Même route que les cartes de voyage (GridCard). Tant que les ventes
      // de test n'ont pas de voyage réel, on renvoie vers la recherche.
      if (offer.tripId && offer.tripDateId) {
        navigate(`/trips/${offer.tripId}/${offer.tripDateId}`);
      } else {
        const city = (offer.destination || "").split(",").pop().trim();
        navigate(`/trips${city ? `?destination=${encodeURIComponent(city)}` : ""}`);
      }
    });
  };

  const count = offers.length;
  const maxDiscount = getMaxDiscount(offers);
  const subtitle = [
    count > 1
      ? t("portal.home.flash.countMany", { count, defaultValue: `${count} offres en cours` })
      : t("portal.home.flash.countOne", "1 offre en cours"),
    maxDiscount > 0 &&
      t("portal.home.flash.upTo", { discount: maxDiscount, defaultValue: `jusqu'à -${maxDiscount}%` }),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section className={styles.section} aria-labelledby="flash-section-title">
      <div className={styles.inner}>
        <SectionHeader
          className={styles.header}
          titleId="flash-section-title"
          kicker={t("portal.home.flash.kicker", "Offres limitées")}
          title={
            <>
              {t("portal.home.flash.titleStart", "Ventes")} <em>{t("portal.home.flash.titleHighlight", "flash")}</em>{" "}
              {t("portal.home.flash.titleEnd", "du moment")}
            </>
          }
          subtitle={subtitle}
        />

        <FlashOfferCarousel
          offers={cards}
          onBook={handleBook}
          onExpire={(card) => expire(card.id)}
          ariaLabel={t("portal.home.flash.carouselLabel", "Ventes flash")}
          t={t}
        />
      </div>
    </section>
  );
}
